import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const DEFAULT_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const toInt = (value, fallback, min = 0) => {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n >= min ? n : fallback;
};

/**
 * Server-side DataTable state kept in the URL, so a page is shareable
 * (`/products?page=2&size=20&search=chanel&sort=name,asc`) and survives
 * browser back/forward and reloads.
 *
 *  - `fetchFn(params)` must resolve to a Spring `Page` ({ content, totalElements, ... }).
 *    `params` = { page, size, search, sort, ...entity filters } — page is 0-indexed.
 *  - `defaultParams` supplies defaults for any key missing from the URL (e.g. { size: 58 }
 *    or { active: 'all' }). A value equal to its default is dropped from the URL to keep it clean.
 *  - any URL key other than page/size/search/sort is treated as an entity filter and passed
 *    through to `fetchFn` as a string.
 *
 * Options: `onError(error)` is called when a fetch fails; `deps` (array, constant length) are extra values
 * that trigger a refetch when they change (e.g. the current store: `{ deps: [currentStore] }`).
 */
export const useDataTable = (fetchFn, defaultParams = {}, options = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ content: [], totalElements: 0 });
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);

  // Latest callbacks/defaults without re-triggering the fetch effect on every render.
  const fetchRef = useRef(fetchFn);
  fetchRef.current = fetchFn;
  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;
  const defaultsRef = useRef(defaultParams);
  defaultsRef.current = defaultParams;

  const searchString = searchParams.toString();

  // Effective params: defaults < URL. page/size are numbers, everything else a string.
  const params = useMemo(() => {
    const defaults = defaultsRef.current;
    const fromUrl = Object.fromEntries(new URLSearchParams(searchString).entries());
    const merged = { search: '', sort: '', ...defaults, ...fromUrl };
    merged.page = toInt(fromUrl.page, toInt(defaults.page, 0), 0);
    merged.size = toInt(fromUrl.size, toInt(defaults.size, DEFAULT_SIZE), 1);
    return merged;
  }, [searchString]);

  // Writes `next` to the URL: empty values and values equal to their default are omitted.
  const writeParams = useCallback(
    (next, { replace = false } = {}) => {
      const defaults = { page: 0, size: DEFAULT_SIZE, search: '', sort: '', ...defaultsRef.current };
      const out = {};
      Object.entries(next).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        if (String(value) === String(defaults[key] ?? '')) return;
        out[key] = String(value);
      });
      setSearchParams(out, { replace });
    },
    [setSearchParams]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.resolve(fetchRef.current(params))
      .then((res) => {
        if (cancelled) return;
        const page = res || {};
        // Deleting the last row of the last page leaves it empty — step back one page.
        if ((page.content || []).length === 0 && params.page > 0 && page.totalElements > 0) {
          const lastPage = Math.max(0, Math.ceil(page.totalElements / params.size) - 1);
          writeParams({ ...params, page: lastPage }, { replace: true });
          return;
        }
        setData({ ...page, content: page.content || [], totalElements: page.totalElements ?? 0 });
      })
      .catch((error) => {
        if (!cancelled && onErrorRef.current) onErrorRef.current(error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `params` is derived from the URL; reloadTick forces a refetch after mutations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString, reloadTick, ...(options.deps || [])]);

  // Search box: the input updates immediately, the URL (and so the request) after a pause.
  const [searchInput, setSearchInput] = useState(params.search);
  useEffect(() => setSearchInput(params.search), [params.search]);
  const searchTimer = useRef(null);
  useEffect(() => () => clearTimeout(searchTimer.current), []);

  const handleSearchChange = useCallback(
    (search) => {
      setSearchInput(search);
      clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(
        () => writeParams({ ...params, search: search.trim(), page: 0 }, { replace: true }),
        SEARCH_DEBOUNCE_MS
      );
    },
    [params, writeParams]
  );

  /** Merge entity-specific filters (e.g. { active: 'inactive' }); resets to the first page. */
  const updateParams = useCallback(
    (newParams) => writeParams({ ...params, ...newParams, page: 0 }),
    [params, writeParams]
  );

  const handlePageChange = useCallback((page) => writeParams({ ...params, page }), [params, writeParams]);

  const handlePageSizeChange = useCallback(
    (size) => writeParams({ ...params, size, page: 0 }),
    [params, writeParams]
  );

  // One call for both so the page-size reset to page 0 isn't clobbered by a stale `params`.
  const handlePaginationModelChange = useCallback(
    ({ page, pageSize }) => {
      if (pageSize !== params.size) writeParams({ ...params, size: pageSize, page: 0 });
      else if (page !== params.page) writeParams({ ...params, page });
    },
    [params, writeParams]
  );

  const handleSortChange = useCallback(
    (sortModel) => {
      const sort = sortModel && sortModel.length > 0 ? `${sortModel[0].field},${sortModel[0].sort}` : '';
      if (sort === params.sort) return;
      writeParams({ ...params, sort, page: 0 });
    },
    [params, writeParams]
  );

  /** Refetch the current page (after a delete / toggle / save). */
  const refresh = useCallback(() => setReloadTick((n) => n + 1), []);

  const sortModel = useMemo(() => {
    if (!params.sort) return [];
    const [field, dir] = params.sort.split(',');
    return field ? [{ field, sort: dir === 'desc' ? 'desc' : 'asc' }] : [];
  }, [params.sort]);

  return {
    data,
    setData,
    loading,
    params,
    searchInput,
    sortModel,
    updateParams,
    handlePageChange,
    handlePageSizeChange,
    handlePaginationModelChange,
    handleSortChange,
    handleSearchChange,
    refresh,
    paginationModel: { page: params.page, pageSize: params.size },
  };
};

export default useDataTable;
